import { Box, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { ReactComponent as CloseIcon } from "../../assets/icons/close-icon.svg";
import DialogComponentStyled from "../styled/dialog.component.styled";
import { MessageItem } from "@/models/apiModels";
import { useTranslation } from "react-i18next";
import authStore from "@/stores/authStore";
import Collapse from "@mui/material/Collapse";
import { formatDateTime } from "@/util";
import api from "@/services/api";
import popupService from "@/services/popupService";

function Row(props: { row: MessageItem }) {
    const { row } = props;
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();
    return (
        <>
            <div className="nofication-message-container">
                <div className="notification-message-header">
                    <div className="message-title">{row.title}</div>
                    <div className="message-creation-date">
                        <div
                            style={{ display: "inline-block", marginInlineEnd: "10px" }}
                            className={row.read ? "status-color-active" : "status-color-inactive"}
                        ></div>
                        {formatDateTime(row.createdAt)}
                    </div>
                </div>
                <div className="notification-message-header-details">
                    <div className="notification-message-from">
                        {t("messages.messageFrom")}: <span>{row.sourceType}</span>
                    </div>

                    {open ? (
                        <span aria-label="expand row" className="link-element" onClick={() => setOpen(!open)}>
                            {t("messages.button.showLess")}
                        </span>
                    ) : (
                        <span aria-label="expand row" className="link-element" onClick={() => setOpen(!open)}>
                            {t("messages.button.showMore")}
                        </span>
                    )}
                </div>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <div className="nofication-details-content">{row.content}</div>
                </Collapse>
            </div>
        </>
    );
}

export interface IMessageDialogTitle {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

function MessageDialogTitle(props: IMessageDialogTitle) {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 0 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

interface INotificationMessagesDialog {
    open: boolean;
    handleClose: () => void;
}

const NotificationMessagesDialog: FC<INotificationMessagesDialog> = (props: INotificationMessagesDialog) => {
    const { open, handleClose } = props;
    const [messages, setMessages] = useState<MessageItem[]>([]);
    const [arentReadMessagesCount, setArentReadMessagesCount] = useState(0);

    const { t } = useTranslation();

    useEffect(() => {
        setMessages(authStore.messages);
        setArentReadMessagesCount(authStore.messages.filter((x) => x.read === false).length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const msgCount = messages.filter((x) => !x.read).length;
        setArentReadMessagesCount(msgCount);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages]);

    const markMessageArRead = async (id: number) => {
        if (!id) {
            return;
        }
        const foundMsg = messages.find((x) => x.id === id);
        if (foundMsg && foundMsg?.read) {
            return;
        }
        try {
            popupService.showLoading();
            const response = await api.markMessageAsRead(id);
            if (response.data.id) {
                authStore.setMessageAsRead(response.data.id);
                const allMsgs = messages.slice();
                const found = allMsgs.find((x) => x.id === id);
                if (found) {
                    found.read = true;
                    authStore.setMessageAsRead(found.id);
                }
                setMessages(allMsgs);
            }
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        }
    };
    return (
        <Box>
            <DialogComponentStyled
                sx={{
                    zIndex: "1050",
                }}
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
                className="notification-messages-dialog"
            >
                <MessageDialogTitle id="customized-dialog-title" onClose={handleClose}>
                    <div className="notification-message-dialog-title">
                        <i
                            style={{ marginInlineEnd: "25px" }}
                            className="icon-f icon-bell fs-26 cursor-pointer p-relative"
                        >
                            {arentReadMessagesCount ? (
                                <span className="message-counter">{arentReadMessagesCount}</span>
                            ) : (
                                ""
                            )}
                        </i>
                        {t("messages.MessagesDialog")}
                    </div>
                </MessageDialogTitle>
                <DialogContent
                    sx={{
                        minWidth: "453px",
                    }}
                    className="notification-messages-content"
                >
                    {messages &&
                        messages.map((row) => (
                            <div key={row.id} onClick={() => markMessageArRead(!row.read ? row.id : 0)}>
                                <Row key={row.id} row={row} />
                            </div>
                        ))}
                </DialogContent>
            </DialogComponentStyled>
        </Box>
    );
};

export default NotificationMessagesDialog;
